// prepareUserProfileMeta.js
import getUserData from '../selector/getUserData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getProfileTdk from '../resource/getProfileTdk.js';
import { getUserPicture } from '../resource/getMediaAsset.js';
import { getI18nInstance } from '../resource/i18n.js';
import getMetaTagsForHelmet from '../resource/getMetaTagsForHelmet.js';
import getUserProfileJsonLd from '../resource/getUserProfileJsonLd.js';
import { BRANDING_NAME } from '../RemoteConfigKeys.js';

/**
 * Prepare meta data for user profile pages
 * @param {Object} options
 * @param {Object} options.state - Redux state
 * @param {string} options.userId - User ID
 * @param {string} options.username - Username (optional, for username-based URLs)
 * @param {string} options.listType - List type (optional)
 * @param {string} options.hostname - Request hostname
 * @param {boolean} options.includeJsonLd - Whether to include JSON-LD data
 * @param {string} options.canonical - Canonical URL (optional, if not provided, will be null)
 * @param {Array} options.alternates - Alternate language links (optional)
 * @param {Object} options.i18n - i18n instance (optional, will use getI18nInstance if not provided)
 * @param {number} options.messageCount - Message count (optional, if provided, will skip fetching, if 0, will add noindex to prevent crawlers from indexing)
 * @returns {Promise<Array>} Meta tags array for Helmet
 */
const prepareUserProfileMeta = async ({
  state,
  userId,
  username: usernameParam,
  listType,
  hostname = 'swag.live',
  includeJsonLd = false,
  canonical = null,
  alternates = null,
  i18n: i18nParam,
  messageCount = 0,
}) => {
  // Get i18n instance - use provided instance or fallback to global
  const { i18n } = i18nParam ? { i18n: i18nParam } : getI18nInstance() || {};
  const t = i18n?.t;

  // Get user data
  const username =
    usernameParam || (userId ? getUserData(state, userId, 'username') : '');
  const biography = userId ? getUserData(state, userId, 'biography') : '';
  const userPicture = userId
    ? getUserPicture({
        userId,
        size: 512,
        format: 'jpg',
      })
    : '';

  // Get TDK (Title, Description, Keywords)
  const { displayTitle, displayDescription, displayKeywords } = getProfileTdk({
    username,
    biography,
    listType,
    t,
  });

  // Get branding name from remote config
  const brandingName = getRemoteConfigData(state, BRANDING_NAME) || 'SWAG';

  // Prepare meta options
  const metaOptions = {
    title: displayTitle,
    description: displayDescription,
    keywords: displayKeywords
      ? displayKeywords.split(',').map(k => k.trim())
      : [],
    images: userPicture ? [{ url: userPicture, width: 512, height: 512 }] : [],
    ogType: 'profile',
    twitterCard: 'summary',
    hostname,
    alternates: {
      canonical,
      alternates,
    },
    url: canonical,
    siteName: brandingName,
    // If user has no messages, add noindex to prevent crawlers from indexing
    robots: messageCount ? [] : ['noindex', 'nofollow'],
  };

  // Add JSON-LD if requested
  if (includeJsonLd && userId) {
    const jsonLdData = await getUserProfileJsonLd({
      state,
      userId,
      username,
      biography,
      type: listType,
      t,
    });
    metaOptions.jsonLd = jsonLdData;
  }

  return getMetaTagsForHelmet(metaOptions);
};

export default prepareUserProfileMeta;
